import { setup } from 'xstate';

import { AdjustInitializationActions } from './adjust-initialization-machine.actions';
import { AdjustInitializationActors } from './adjust-initialization-machine.actors';
import { type AdjustEvent } from './adjust-initialization-machine.events';
import { AdjustInitializationGuards } from './adjust-initialization-machine.guards';

/**
 * Adjust SDK is disabled if the feature flag is disabled or if the customer disallows tracking via OneTrust.
 *
 * When enabled, it handles attribution from marketing campaigns.
 * It also handles special Adjust deep links and deferred deep links.
 *
 * # Adjust Deep Links.
 *
 * In the Adjust dashboard, we can configure deep links with campaign attribution params.
 * i.e. https://sweetgreen.go.link/rewards-test was configured as sweetgreen-order://rewards
 *
 * Opening the link above won't be handled by the router directly (i.e. open /rewards-test and get not found).
 * Instead it is rewritten by +native-intent.tsx to /, so it opens the home screen and initializes the Adjust machine.
 * Note: if the app is already open, it routes to null so nothing happens and they stay on the present screen.
 *
 * Once the machine is ready, it invokes `listenForAdjustLinks` to start listening for Adjust deep links.
 * It uses Linking.getInitialURL and Linking.addEventListener to access the deep links, either cold or hot.
 * These are sent to the Adjust SDK `processAndResolveDeeplink` which attributes back to Adjust, and returns the real link.
 * Now the real link (sweetgreen-order://rewards) is sent to Linking.openURL(url) so the router handles it like any other URL.
 *
 * # Adjust Deferred Deep Links.
 *
 * If the app is not installed, the customer will be taken to the app store.
 * After the app is installed the SDK will initialize and notice there is a deferred deep link.
 * It will automatically unwrap and open the link via the OS, which works since it will use the configured url.
 */
export const adjustInitializationMachine = setup({
  types: { events: {} as AdjustEvent },
  actions: AdjustInitializationActions,
  actors: AdjustInitializationActors,
  guards: AdjustInitializationGuards,
}).createMachine({
  initial: 'waiting',
  on: {
    DEEPLINK_RESOLVED: {
      actions: ['openDeeplink'],
    },
  },
  states: {
    waiting: {
      on: {
        SYNC_CUSTOMER: {
          guard: 'canTrack',
          target: 'ready',
          actions: ['initialize', 'enable', 'syncCustomer'],
        },
      },
    },
    ready: {
      invoke: { src: 'listenForAdjustLinks' },
      on: {
        SYNC_CUSTOMER: [
          {
            guard: 'canTrack',
            actions: ['syncCustomer'],
          },
          {
            target: 'disabled',
            actions: ['clearCustomer', 'disable'],
          },
        ],
        DEEPLINK_RECEIVED: {
          actions: ['resolveDeeplink'],
        },
      },
    },
    disabled: {
      on: {
        SYNC_CUSTOMER: {
          guard: 'canTrack',
          target: 'ready',
          actions: ['enable', 'syncCustomer'],
        },
      },
    },
  },
});
